import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api";

/**
 * POST /api/auth/register-tenant (superadmin, see Settings) returns an
 * inviteToken in its JSON response -- no email is sent by the backend -- so
 * this screen is the other half of that loop: paste the token, set a
 * password, then sign in normally. Linked from Login and from Settings'
 * register-tenant result panel.
 */
export function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const result = await api.post<{ ok: boolean; email: string }>("/api/auth/accept-invite", {
        token,
        password,
      });
      setDone(result.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not accept invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="card login-card">
        <h1>Accept invite</h1>
        {done ? (
          <div className="modal-body">
            <p>Password set for {done}. You can now sign in.</p>
            <button className="primary" type="button" onClick={() => navigate("/login")}>
              Go to sign in
            </button>
          </div>
        ) : (
          <form className="modal-body" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="token">Invite token</label>
              <input
                id="token"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pw">New password</label>
              <input
                id="pw"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pw2">Confirm password</label>
              <input
                id="pw2"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {error && <div className="error-text">{error}</div>}
            <button className="primary" type="submit" disabled={busy}>
              {busy ? "Setting password..." : "Set password"}
            </button>
            <div className="muted">
              <Link to="/login">Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
